import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Custom hook for scroll-triggered animations
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

interface Pillar {
  kicker: string;
  title: string;
  bullets: { lead: string; rest: string }[];
  image: string;
  imageAlt: string;
}

const pillars: Pillar[] = [
  {
    kicker: 'Learning Paths · Daily bites',
    title: 'Never wonder what to learn next',
    bullets: [
      { lead: 'Follow a project-first path', rest: ' where every lesson feeds the app you ship at the end.' },
      { lead: 'Learn in daily bites', rest: ' — one node a day, each opening in chat, a tutorial, or the project builder, whatever teaches best.' },
      { lead: 'Track your progress', rest: ' node by node until the final build is done.' },
    ],
    image: '/us-landing/paths-graph.png',
    imageAlt: 'Learning path graph for a Python trading app with step-by-step nodes',
  },
  {
    kicker: 'Bookmate · Interview cram',
    title: 'Tutorials that read like a book and run like a lab',
    bullets: [
      { lead: 'Ask for any topic', rest: ' — pointers, system design, pandas — and get a full tutorial with narrative, runnable code, and exercises on one page.' },
      { lead: 'Run every code block', rest: ' in your browser and see the real output right under the code.' },
      { lead: 'Compare two languages', rest: ' side by side. Know Java but interviewing in Python? Run both versions of the same idea.' },
    ],
    image: '/us-landing/bookmate-cpp-rust.png',
    imageAlt: 'Bookmate tutorial comparing C++ and Rust with runnable code blocks',
  },
  {
    kicker: 'Capstone · Portfolio projects',
    title: 'Describe a project and watch AI build it',
    bullets: [
      { lead: 'Type one line', rest: ' like "build a Streamlit trading app" and Capstone plans the build, writes the files, and runs them in a real cloud workspace.' },
      { lead: 'Watch every step', rest: ' as the plan, the code, and the terminal output appear side by side.' },
      { lead: 'Download the full workspace', rest: ' as a zip and keep building on your own machine.' },
    ],
    image: '/us-landing/capstone-simtrade.png',
    imageAlt: 'SimTrade Lab trading app built and running inside Cubemate Capstone',
  },
  {
    kicker: 'MLmate · Machine learning',
    title: 'See machine learning happen, not just read about it',
    bullets: [
      { lead: 'Pick a real problem', rest: ' like customer churn or fraud detection and watch an actual model train on it.' },
      { lead: 'Move the sliders', rest: " to change a customer's numbers and see the prediction update live." },
      { lead: 'Download the code', rest: ' behind every project and study exactly what ran.' },
    ],
    image: '/us-landing/mlmate-churn.png',
    imageAlt: 'MLmate customer churn prediction with live sliders',
  },
];

const LandingPageUS: React.FC = () => {
  const navigate = useNavigate();

  // Track ad visit on page load — capture UTM params from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = params.get('utm_source') || params.get('gclid');
    if (hasUtm) {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      fetch(`${apiUrl}/api/ad-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          landing_page: '/us',
          utm_source: params.get('utm_source') || undefined,
          utm_medium: params.get('utm_medium') || undefined,
          utm_campaign: params.get('utm_campaign') || undefined,
          utm_content: params.get('utm_content') || undefined,
          utm_term: params.get('utm_term') || undefined,
          gclid: params.get('gclid') || undefined,
          referrer: document.referrer || undefined,
        }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.visit_id) {
            localStorage.setItem('ad_visit_id', String(data.visit_id));
          }
        })
        .catch(() => {});
    }
  }, []);

  // Section animation hooks
  const pillarViews = [useInView(), useInView(), useInView(), useInView()];
  const testimonialsSection = useInView();
  const ctaSection = useInView();

  // Testimonials auto-scroll
  const [testimonialOffset, setTestimonialOffset] = useState(0);
  const testimonials = [
    {
      name: 'Alex R.',
      role: 'Software Developer, San Francisco',
      avatar: '/testimonials/us-1.jpg',
      text: 'Three interviews in one week and system design was my weak spot. Asked Cubemate for a tutorial on every gap and ran the code until I could explain it. Nailed all three — two offers.',
    },
    {
      name: 'Sarah L.',
      role: 'CS Student, Georgia Tech',
      avatar: '/testimonials/us-2.jpg',
      text: 'Coming from Java, I learned Python with the side-by-side view — same idea, both languages, run both. Fastest I\'ve ever picked up a language.',
    },
    {
      name: 'Jordan K.',
      role: 'Bootcamp Grad, NYC',
      avatar: '/testimonials/us-3.jpg',
      text: 'Typed "build a Streamlit trading app" and watched Capstone plan it, write the files, and run it live. Downloaded the whole workspace — it\'s the first project on my portfolio now.',
    },
    {
      name: 'Marcus T.',
      role: 'Career Switcher, Michigan',
      avatar: '/testimonials/us-4.jpg',
      text: 'Twenty minutes a night, one node of my learning path at a time. Every lesson fed the app at the end — eight weeks in, I\'d shipped it. It\'s the first thing recruiters ask about.',
    },
    {
      name: 'Priya M.',
      role: 'Data Analyst, Austin',
      avatar: '/testimonials/us-5.jpg',
      text: 'Read about machine learning for months and it never stuck. On Cubemate I watched a churn model actually train, moved the sliders, saw predictions change — it finally clicked.',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setTestimonialOffset((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      visible.push(testimonials[(testimonialOffset + i) % testimonials.length]);
    }
    return visible;
  };

  const handleCTA = () => {
    // Meta campaign optimizes on Lead — this event must match the ad set's conversion event
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: 'us_landing_cta' });
    }
    // Fire Google Ads conversion
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-18013772297/FiwJCN68lIocEIm00Y1D',
        value: 1.0,
        currency: 'USD',
      });
    }
    // from=us switches the login page to trial-focused copy
    navigate('/login?from=us');
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">
      {/* ── Navigation ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="/cubemate-logo.png" alt="Cubemate" className="w-9 h-9 rounded-lg" />
              <span className="text-xl font-bold text-gray-900">Cubemate</span>
            </div>
            <button
              onClick={handleCTA}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors"
            >
              Start Free
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero Section ── */}
      <section className="relative pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight mb-6">
            Experience a new way of learning.{' '}
            <span className="text-violet-600 block">Like never before.</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Learn any coding topic while you build a real app — every lesson runs live in your browser and feeds the app you ship at the end.
          </p>
          <div className="mt-8">
            <button
              onClick={handleCTA}
              className="px-8 py-4 text-lg font-bold rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-all shadow-lg shadow-violet-600/25 hover:scale-105 transform"
            >
              Start Learning Free
            </button>
            <p className="mt-3 text-sm text-gray-500">Sign up free — no card needed · 7-day free Pro trial available · cancel anytime</p>
          </div>
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="flex -space-x-2">
              {testimonials.slice(0, 4).map((t) => (
                <img
                  key={t.name}
                  src={t.avatar}
                  alt={t.name}
                  className="w-8 h-8 rounded-full object-cover border-2 border-white"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=7c3aed&color=fff&size=64`;
                  }}
                />
              ))}
            </div>
            <p className="text-sm text-gray-500 text-left">
              Loved by learners prepping interviews and shipping portfolio projects
            </p>
          </div>
        </div>
      </section>

      {/* ── Positioning line ── */}
      <section className="pt-4 pb-6 lg:pb-10 px-4 sm:px-6 lg:px-8">
        <p className="max-w-3xl mx-auto text-center text-xl sm:text-2xl text-gray-700 font-medium">
          Most AI tools hand you code and leave you to figure it out.{' '}
          <span className="text-gray-900 font-bold">Cubemate turns anything you want to learn into something you can run.</span>
        </p>
      </section>

      {/* ── Pillar Sections ── */}
      {pillars.map((pillar, idx) => {
        const view = pillarViews[idx];
        const imageLeft = idx % 2 === 1;
        return (
          <React.Fragment key={pillar.kicker}>
          <section className={`py-10 lg:py-16 px-4 sm:px-6 lg:px-8 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
            <div
              ref={view.ref}
              className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center transition-all duration-700 ${view.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className={imageLeft ? 'lg:order-2' : ''}>
                <p className="text-sm font-semibold uppercase tracking-wide text-violet-600 mb-3">{pillar.kicker}</p>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6 leading-tight">{pillar.title}</h2>
                <ul className="space-y-4">
                  {pillar.bullets.map((b) => (
                    <li key={b.lead} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <p className="text-gray-600">
                        <span className="font-bold text-gray-900">{b.lead}</span>
                        {b.rest}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={imageLeft ? 'lg:order-1' : ''}>
                <img
                  src={pillar.image}
                  alt={pillar.imageAlt}
                  loading="lazy"
                  className="w-full rounded-xl border border-gray-200 shadow-xl shadow-gray-300/40"
                />
              </div>
            </div>
          </section>
          {/* First CTA — only after Learning Paths + Bookmate have made the case */}
          {idx === 1 && (
            <section className="py-10 lg:py-14 px-4 sm:px-6 lg:px-8 text-center">
              <button
                onClick={handleCTA}
                className="px-8 py-4 text-lg font-bold rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-all shadow-lg shadow-violet-600/25 hover:scale-105 transform"
              >
                Start Learning Free
              </button>
              <p className="mt-3 text-sm text-gray-500">Sign up free — no card needed · 7-day free Pro trial available · cancel anytime</p>
            </section>
          )}
          </React.Fragment>
        );
      })}

      {/* ── Testimonials Section ── */}
      <section className="py-12 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div
          ref={testimonialsSection.ref}
          className={`max-w-6xl mx-auto transition-all duration-700 ${testimonialsSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            Loved by <span className="text-violet-600">Cubemate users</span>
          </h2>
          <p className="text-gray-500 text-center mb-10">Real stories from real people.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {getVisibleTestimonials().map((t, i) => (
              <div
                key={`${testimonialOffset}-${i}`}
                className="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-500 animate-fade-in"
              >
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-100"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=7c3aed&color=fff&size=96`;
                    }}
                  />
                  <div>
                    <p className="text-gray-900 font-semibold text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.role}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed italic">
                  "{t.text}"
                </p>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setTestimonialOffset(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === testimonialOffset ? 'bg-violet-600 w-6' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Testimonial group ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="py-14 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div
          ref={ctaSection.ref}
          className={`max-w-3xl mx-auto text-center transition-all duration-700 ${ctaSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Start free. <span className="text-violet-600">Learn something today.</span>
          </h2>
          <p className="text-gray-600 mb-8">
            Your first tutorial is minutes away — pick a topic, run the code, understand it.
          </p>
          <button
            onClick={handleCTA}
            className="px-8 py-4 text-lg font-bold rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-all shadow-lg shadow-violet-600/25 hover:scale-105 transform"
          >
            Start Learning Free
          </button>
          <p className="mt-3 text-sm text-gray-500">Sign up free — no card needed · 7-day free Pro trial available · cancel anytime</p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 px-4 border-t border-gray-200">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <img src="/corestrat-logo.png" alt="CorestratAI Solutions" className="w-6 h-6 rounded" />
            <span>© {new Date().getFullYear()} CorestratAI Solutions</span>
          </div>
          <div className="flex gap-6">
            <a href="/privacy" className="hover:text-gray-900 transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-gray-900 transition-colors">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageUS;
